/**
 * AuthProvider + useAuth — implements AuthContextType / AuthSession from contracts.
 *
 * On mount the provider hydrates from GET /auth/session (Cognito ID-token cookie).
 * Login uses POST /auth/cognito-login. Access-token lives in memory as Bearer backup.
 *
 * TENANT-FIRST (Decision §1): `register` is implemented here but the Account
 * step does not call it — the orchestrator registers the user during provisioning.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

import type { AuthContextType, AuthSession, LoginCredentials, RegisterCredentials, UserProfile } from '@/contracts/auth';
import * as authService from '@/features/onboarding/services/auth.service';
import { setAccessToken, setUnauthorizedHandler } from '@/features/onboarding/services/http-client';

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

const SIGNED_OUT: AuthSession = { isAuthenticated: false, isLoading: false, accessToken: null, user: null, groups: [] };

export interface AuthProviderProps {
  children: ReactNode;
  /** Runs after an explicit logout (not after an expired session), e.g. to forget locally stored operator data. */
  onLogout?: () => void;
}

export function AuthProvider({ children, onLogout }: AuthProviderProps) {
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;
  const [state, setState] = useState<AuthSession>({
    isAuthenticated: false,
    isLoading: true,
    accessToken: null,
    user: null,
    groups: [],
  });

  // Hydrate operator session from the HttpOnly Cognito ID-token cookie.
  useEffect(() => {
    let cancelled = false;

    async function hydrateSession() {
      try {
        const session = await authService.fetchSession();
        if (cancelled) return;

        setState({
          isAuthenticated: true,
          isLoading: false,
          accessToken: null,
          user: session.email
            ? ({
                id: session.email,
                email: session.email,
                emailVerified: true,
                status: 'active',
                profile: {},
                marketingPreferences: { emailOptIn: false, smsOptIn: false },
                hasPassword: true,
                linkedAccounts: [],
                createdAt: '',
                updatedAt: '',
              } as UserProfile)
            : null,
          groups: session.groups,
        });
      } catch {
        if (cancelled) return;
        setState({
          isAuthenticated: false,
          isLoading: false,
          accessToken: null,
          user: null,
          groups: [],
        });
      }
    }

    hydrateSession();
    return () => {
      cancelled = true;
    };
  }, []);

  // A 401 from any session call means the ID token expired (about an hour, no refresh): sign out locally so the
  // login gate re-prompts, and drop the stale Bearer copy. Idempotent: many calls may fail at once.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setAccessToken(null);
      setState((prev) => (prev.isAuthenticated ? SIGNED_OUT : prev));
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    const result = await authService.cognitoLogin(credentials);
    // The login answer carries no groups: ask the session for them (best effort, the role label is cosmetic).
    let groups: string[] = [];
    try {
      groups = (await authService.fetchSession())?.groups ?? [];
    } catch {
      // No groups: the account row simply shows no role.
    }
    setState((prev) => ({
      ...prev,
      isAuthenticated: true,
      accessToken: result.tokens.accessToken,
      user: {
        id: credentials.email,
        email: credentials.email,
        emailVerified: true,
        status: 'active',
        profile: { firstName: result.user.displayName },
        marketingPreferences: { emailOptIn: false, smsOptIn: false },
        hasPassword: true,
        linkedAccounts: [],
        createdAt: '',
        updatedAt: '',
      },
      groups,
    }));
  }, []);

  const register = useCallback(async (credentials: RegisterCredentials): Promise<void> => {
    // Per TENANT-FIRST Decision §1: registration happens via the orchestrator
    // during provisioning. This method exists for completeness / future flows.
    await authService.register(credentials);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const result = await authService.refresh();
    const token = result.tokens.accessToken;

    setState((prev) => ({
      ...prev,
      isAuthenticated: true,
      accessToken: token,
    }));
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch {
      // Logout call failed — clear local state regardless
    }

    setState(SIGNED_OUT);
    onLogoutRef.current?.();
  }, []);

  const getProfile = useCallback(async (): Promise<UserProfile> => {
    if (!state.accessToken) {
      throw new Error('Not authenticated');
    }
    const user = await authService.getProfile(state.accessToken);
    setState((prev) => ({ ...prev, user }));
    return user;
  }, [state.accessToken]);

  const value = useMemo<AuthContextType>(
    () => ({
      ...state,
      login,
      register,
      refresh,
      logout,
      getProfile,
    }),
    [state, login, register, refresh, logout, getProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
